import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import { endpoints, isTerminal } from "../api.js";
import { Icon } from "../components/Icon.jsx";
import { ReportDialog } from "../components/ReportDialog.jsx";
import { Banner, EmptyState, ErrorBanner, Loading, Modal, Spinner, StatusPill } from "../components/ui.jsx";
import { useDocumentTitle, useErrorMessage, useLocalePath } from "../i18n/hooks.js";
import { canReview as roleCanReview, useSession } from "../session.jsx";
import { useWorkspace } from "../workspace.jsx";
import { ContextMenu, FindingsTable } from "./results/FindingsTable.jsx";
import { ObjectsPanel } from "./results/ObjectsPanel.jsx";
import { PropertiesPanel } from "./results/PropertiesPanel.jsx";
import { StagePipeline } from "./results/StagePipeline.jsx";
import { useJobStream } from "./results/useJobStream.js";

const GraphView = lazy(() => import("./results/GraphView.jsx"));

function NoteDialog({ initial, count, onSave, onClose }) {
  const intl = useIntl();
  const [note, setNote] = useState(initial || "");
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      title={intl.formatMessage({ id: "results.noteTitle" }, { count })}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            <FormattedMessage id="common.cancel" />
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              const ok = await onSave(note);
              if (ok) onClose();
              else setBusy(false);
            }}
          >
            {busy ? <Spinner size={16} /> : null}
            <FormattedMessage id="common.save" />
          </button>
        </>
      }
    >
      <label className="field">
        <span className="field-label">
          <FormattedMessage id="results.note" />
        </span>
        <textarea rows={5} maxLength={4000} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
    </Modal>
  );
}

function JobViewInner() {
  const { jobId } = useParams();
  const intl = useIntl();
  const lp = useLocalePath();
  const errMsg = useErrorMessage();
  const { authConfig, role, refreshTenant } = useSession();
  const { view, search, trackJob } = useWorkspace();
  const { job, setJob, error, transport } = useJobStream(jobId, { preferSse: authConfig?.mode !== "oidc" });
  useDocumentTitle("job.title");

  const [investigation, setInvestigation] = useState(null);
  const [findings, setFindings] = useState(null);
  const [graph, setGraph] = useState(null);
  const [resultsError, setResultsError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [active, setActive] = useState(null); // { kind: "finding" | "node", id }
  const [menu, setMenu] = useState(null); // { x, y, ids }
  const [noteFor, setNoteFor] = useState(null); // ids
  const [reportFor, setReportFor] = useState(null); // { count }
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [notice, setNotice] = useState(null);
  const terminalSeen = useRef(false);

  const canReview = roleCanReview(role);
  const terminal = job ? isTerminal(job.status) : false;

  // Investigation header info.
  useEffect(() => {
    if (!job?.investigationId || investigation?.id === job.investigationId) return;
    endpoints.investigation(job.investigationId).then(
      (d) => setInvestigation(d.investigation),
      () => {},
    );
  }, [job?.investigationId, investigation?.id]);

  // Keep the tasks indicator in sync.
  useEffect(() => {
    if (job) trackJob(job, investigation);
  }, [job, investigation, trackJob]);

  const loadResults = useCallback(() => {
    setResultsError(null);
    Promise.all([endpoints.findings(jobId), endpoints.graph(jobId)]).then(
      ([f, g]) => {
        setFindings(f.findings || []);
        setGraph({ nodes: g.nodes || [], edges: g.edges || [] });
      },
      setResultsError,
    );
  }, [jobId]);

  // On reaching a terminal state: load results, refresh credits (refunds), fold the pipeline.
  useEffect(() => {
    if (!terminal || terminalSeen.current) return;
    terminalSeen.current = true;
    refreshTenant();
    if (job.status !== "denied") loadResults();
    setCollapsed(["succeeded", "partial"].includes(job.status));
  }, [terminal, job, loadResults, refreshTenant]);

  const q = search.trim().toLowerCase();
  const visibleFindings = useMemo(() => {
    if (!findings) return [];
    if (!q) return findings;
    return findings.filter((f) =>
      [f.value, f.type, f.note, f.reviewState].some((v) => String(v || "").toLowerCase().includes(q)),
    );
  }, [findings, q]);
  const visibleNodes = useMemo(() => {
    if (!graph) return [];
    if (!q) return graph.nodes;
    return graph.nodes.filter((n) => `${n.label} ${n.type}`.toLowerCase().includes(q));
  }, [graph, q]);

  const findingById = useMemo(() => new Map((findings || []).map((f) => [f.id, f])), [findings]);

  const review = useCallback(
    async (ids, reviewState, note) => {
      setReviewBusy(true);
      setReviewError(null);
      try {
        const results = await Promise.all(
          ids.map((id) => {
            const f = findingById.get(id);
            const body = { reviewState: reviewState ?? f?.reviewState ?? "unreviewed" };
            if (note !== undefined) body.note = note;
            return endpoints.patchFinding(id, body);
          }),
        );
        const updated = new Map(results.map((r) => [r.finding.id, r.finding]));
        setFindings((list) => list.map((f) => updated.get(f.id) || f));
        return true;
      } catch (e) {
        setReviewError(e);
        return false;
      } finally {
        setReviewBusy(false);
      }
    },
    [findingById],
  );

  const cancel = async () => {
    setCancelling(true);
    setCancelError(null);
    try {
      const { job: j } = await endpoints.cancelJob(jobId);
      setJob(j);
    } catch (e) {
      setCancelError(e);
    } finally {
      setCancelling(false);
    }
  };

  const openMenu = (e, f) => {
    const ids = selected.has(f.id) && selected.size > 1 ? [...selected] : [f.id];
    setActive({ kind: "finding", id: f.id });
    setMenu({ x: e.clientX, y: e.clientY, ids });
  };

  const menuItems = menu
    ? [
        {
          id: "accept",
          icon: "check",
          label: intl.formatMessage({ id: "results.menu.accept" }, { count: menu.ids.length }),
          disabled: !canReview,
          onSelect: () => review(menu.ids, "accepted"),
        },
        {
          id: "reject",
          icon: "x",
          label: intl.formatMessage({ id: "results.menu.reject" }, { count: menu.ids.length }),
          disabled: !canReview,
          onSelect: () => review(menu.ids, "rejected"),
        },
        {
          id: "note",
          icon: "note",
          label: intl.formatMessage({ id: "results.addNote" }),
          disabled: !canReview,
          onSelect: () => setNoteFor(menu.ids),
        },
        { separator: true },
        {
          id: "methods",
          icon: "play",
          label: intl.formatMessage({ id: "results.menu.runMethods" }),
          tag: intl.formatMessage({ id: "common.comingSoon" }),
          disabled: true,
        },
        {
          id: "report",
          icon: "file",
          label: intl.formatMessage({ id: "results.menu.addToReport" }),
          onSelect: () => setReportFor({ count: menu.ids.length }),
        },
      ]
    : [];

  if (error && !job) {
    return (
      <div className="page">
        <ErrorBanner message={errMsg(error)} />
      </div>
    );
  }
  if (!job) return <div className="page"><Loading /></div>;

  const activeFinding = active?.kind === "finding" ? findingById.get(active.id) : null;
  const activeNode = active?.kind === "node" ? graph?.nodes.find((n) => n.id === active.id) : null;
  const hasResults = findings !== null && graph !== null;

  return (
    <div className="job-page">
      <header className="job-head">
        <div className="job-title">
          <Link to={lp(investigation ? `/investigations/${investigation.id}` : "/investigations")} className="back-link">
            <Icon name="arrowLeft" size={15} />
            {investigation ? investigation.name : <FormattedMessage id="nav.investigations" />}
          </Link>
          <div className="meta-line">
            {investigation ? (
              <>
                <span className={`type-badge type-${investigation.targetType}`}>
                  <FormattedMessage id={`targetType.${investigation.targetType}`} defaultMessage={investigation.targetType} />
                </span>
                <strong className="mono job-target">{investigation.target}</strong>
              </>
            ) : null}
            <StatusPill ns="jobStatus" value={job.status} />
            {transport === "sse" && !terminal ? (
              <span className="live-tag">
                <span className="pulse-dot" />
                <FormattedMessage id="job.live" />
              </span>
            ) : null}
          </div>
        </div>
        <dl className="job-stats">
          <div>
            <dt>
              <FormattedMessage id="job.cost" />
            </dt>
            <dd className="mono">
              {job.actualCost != null ? (
                <FormattedNumber value={job.actualCost} />
              ) : (
                <FormattedMessage id="job.estCost" values={{ n: job.estCost ?? 0 }} />
              )}
            </dd>
          </div>
          <div>
            <dt>
              <FormattedMessage id="job.started" />
            </dt>
            <dd>
              <FormattedDate value={job.createdAt} dateStyle="short" timeStyle="short" />
            </dd>
          </div>
          {job.finishedAt ? (
            <div>
              <dt>
                <FormattedMessage id="job.finished" />
              </dt>
              <dd>
                <FormattedDate value={job.finishedAt} dateStyle="short" timeStyle="short" />
              </dd>
            </div>
          ) : null}
        </dl>
        <div className="btn-row">
          {!terminal ? (
            <button
              type="button"
              className="btn btn-danger-outline"
              onClick={cancel}
              disabled={cancelling || job.cancelRequested}
            >
              {cancelling ? <Spinner size={15} /> : <Icon name="stop" size={14} />}
              <FormattedMessage id={job.cancelRequested ? "job.cancelRequested" : "job.cancel"} />
            </button>
          ) : ["succeeded", "partial"].includes(job.status) ? (
            <button type="button" className="btn btn-outline" onClick={() => setReportFor({ count: 0 })}>
              <Icon name="file" size={15} />
              <FormattedMessage id="reports.generateTitle" />
            </button>
          ) : null}
        </div>
      </header>

      <ErrorBanner message={cancelError ? errMsg(cancelError) : null} />
      {notice ? (
        <Banner kind="ok" icon="check" action={
          investigation ? (
            <Link className="btn btn-ghost btn-sm" to={lp(`/investigations/${investigation.id}`)}>
              <FormattedMessage id="reports.viewAll" />
            </Link>
          ) : null
        }>
          {notice}
        </Banner>
      ) : null}
      {job.status === "denied" ? (
        <Banner kind="error" icon="shield">
          <FormattedMessage id="error.policy_denied" />
          {job.error ? <span className="muted"> — {job.error}</span> : null}
        </Banner>
      ) : null}
      {job.status === "failed" ? (
        <Banner kind="error" icon="alert">
          <FormattedMessage id="job.failed" />
          {job.error ? <span className="muted"> — {job.error}</span> : null}
        </Banner>
      ) : null}
      {job.status === "partial" ? (
        <Banner kind="warn" icon="alert">
          <FormattedMessage id="job.partial" />
        </Banner>
      ) : null}

      <StagePipeline job={job} collapsed={collapsed} onToggle={terminal ? () => setCollapsed((c) => !c) : null} />

      {!terminal ? (
        <section className="card results-pending">
          <Spinner size={22} />
          <div>
            <strong>
              <FormattedMessage id="job.runningTitle" />
            </strong>
            <p className="muted small">
              <FormattedMessage id="job.runningBody" />
            </p>
          </div>
        </section>
      ) : job.status === "denied" ? null : resultsError ? (
        <ErrorBanner message={errMsg(resultsError)} onRetry={loadResults} />
      ) : !hasResults ? (
        <Loading />
      ) : findings.length === 0 && graph.nodes.length === 0 ? (
        <div className="card">
          <EmptyState icon="search" title={<FormattedMessage id="results.emptyTitle" />} body={<FormattedMessage id="results.emptyBody" />} />
        </div>
      ) : (
        <section className={`results ${view === "graph" ? "is-graph" : "is-table"}`}>
          {view === "graph" ? (
            <ObjectsPanel
              nodes={visibleNodes}
              activeId={active?.kind === "node" ? active.id : null}
              onSelect={(id) => setActive({ kind: "node", id })}
            />
          ) : null}
          <div className="results-main panel">
            {view === "graph" ? (
              <Suspense fallback={<Loading />}>
                <GraphView
                  graph={graph}
                  search={search}
                  selectedId={active?.kind === "node" ? active.id : null}
                  onSelect={(id) => setActive(id ? { kind: "node", id } : null)}
                />
              </Suspense>
            ) : visibleFindings.length === 0 ? (
              <p className="muted pad center-text">
                <FormattedMessage id="common.noMatches" values={{ q: search }} />
              </p>
            ) : (
              <FindingsTable
                findings={visibleFindings}
                selected={selected}
                activeId={active?.kind === "finding" ? active.id : null}
                onActivate={(id) => setActive({ kind: "finding", id })}
                onToggle={(id) =>
                  setSelected((s) => {
                    const n = new Set(s);
                    if (n.has(id)) n.delete(id);
                    else n.add(id);
                    return n;
                  })
                }
                onToggleAll={(on) => setSelected(on ? new Set(visibleFindings.map((f) => f.id)) : new Set())}
                onContextMenu={openMenu}
              />
            )}
          </div>
          <PropertiesPanel
            active={active}
            finding={activeFinding}
            node={activeNode}
            graph={graph}
            findings={findings}
            canReview={canReview}
            onReview={review}
            onNote={(ids) => setNoteFor(ids)}
            reviewBusy={reviewBusy}
            reviewError={reviewError}
            onSelectNode={(id) => setActive({ kind: "node", id })}
            onSelectFinding={(id) => setActive({ kind: "finding", id })}
          />
          <footer className="statusbar">
            <span>
              <FormattedMessage id="results.status.findings" values={{ count: findings.length }} />
            </span>
            <span>
              <FormattedMessage id="results.status.nodes" values={{ count: graph.nodes.length }} />
            </span>
            {selected.size ? (
              <span className="accent-text">
                <FormattedMessage id="results.status.selected" values={{ count: selected.size }} />
              </span>
            ) : null}
            <span className="grow" />
            {view === "table" ? (
              <span className="muted">
                <FormattedMessage id="results.status.rightClick" />
              </span>
            ) : null}
          </footer>
        </section>
      )}

      {menu ? <ContextMenu x={menu.x} y={menu.y} items={menuItems} onClose={() => setMenu(null)} /> : null}
      {noteFor ? (
        <NoteDialog
          count={noteFor.length}
          initial={noteFor.length === 1 ? findingById.get(noteFor[0])?.note : ""}
          onClose={() => setNoteFor(null)}
          onSave={(note) => review(noteFor, undefined, note)}
        />
      ) : null}
      {reportFor && investigation ? (
        <ReportDialog
          investigationId={investigation.id}
          jobId={job.id}
          findingCount={reportFor.count}
          onClose={() => setReportFor(null)}
          onCreated={() => setNotice(intl.formatMessage({ id: "reports.created" }))}
        />
      ) : null}
    </div>
  );
}

/** Remount per job so per-job state (selection, terminal handling) never leaks across jobs. */
export default function JobView() {
  const { jobId } = useParams();
  return <JobViewInner key={jobId} />;
}
